import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'core/services/language.service';
import { resolveLanguage } from 'core/services/supported-language';
import moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-date',
  styles: [`
               mat-icon {
                   font-size: 24px;
                   margin-right: 5px;
                   height: 24px;
                   width: 24px;
                   flex-shrink: 0;
               }

               .date-content {
                   display: flex;
                   align-items: center;
               }

               :host {
                   display: inline-block;
               }

               .heading-container {
                   text-align: inherit;
               }

               .sub-title {
      color: rgba(0, 0, 0, 0.5);
    }

    .center {
      align-self: center;
    }

  `],
  template: `
    <div [matTooltip]="tooltip || null" class="date-content">
      @if (icon) {
        <mat-icon color="primary">{{icon}}</mat-icon>
      }
      <div class="heading-container">
        @if (isValid) {
          <div class="center">{{ display }}</div>
          @if (needAgo) {
            <div class="sub-title">{{ dateAgo }}</div>
          }
        } @else {
          <app-colorized-text>{{displayPlaceholder}}</app-colorized-text>
        }
      </div>
    </div>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateComponent implements OnChanges, OnDestroy {
  @Input() date!: string;
  @Input() icon?: string;
  @Input() tooltip?: string;
  @Input() needAgo = true;

  @Input() placeholder?: string;
  isValid = false;

  dateAgo?: string;
  display = '';

  private readonly languageSubscription: Subscription;

  constructor(private translate: TranslateService, private language: LanguageService, private cd: ChangeDetectorRef) {
    this.languageSubscription = this.translate.onLangChange.subscribe(() => {
      this.refresh();
      this.cd.markForCheck();
    });
  }

  get displayPlaceholder(): string {
    return this.placeholder ?? this.translate.instant('COMMON.no_value');
  }

  ngOnChanges(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.languageSubscription.unsubscribe();
  }

  private refresh(): void {
    const date = this.date?.trim()
      ? moment.utc(this.date).local().locale(resolveLanguage(this.translate.currentLang, this.language.currentLanguage))
      : moment.invalid();
    this.isValid = date.isValid();

    if (!this.isValid) {
      this.display = '';
      this.dateAgo = undefined;
      return;
    }

    this.display = date.format('YYYY-MM-DD H:mm');
    this.dateAgo = date.fromNow();
  }
}
